// src/pages/LMS/Analytics/CourseAnalytics.js
import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Grid, Card, CardContent, Button,
  FormControl, InputLabel, Select, MenuItem, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, LinearProgress, IconButton, Alert
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Download as DownloadIcon,
  TrendingUp as TrendingIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import axiosInstance from '../../../utils/axiosInstance';

const CourseAnalytics = () => {
  const { chatbotId, courseId } = useParams(); // courseId can be C-0000001 or numeric
  const navigate = useNavigate();

  const [course, setCourse] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState('30_days');

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

  // Helper functions for safe data handling
  const safeNumber = (value, defaultValue = 0) => {
    const num = Number(value);
    return isNaN(num) ? defaultValue : num;
  };

  const safeToFixed = (value, decimals = 1, defaultValue = 0) => {
    const num = safeNumber(value, defaultValue);
    return num.toFixed(decimals);
  };

  const safeArray = (value) => (Array.isArray(value) ? value : []);

  useEffect(() => {
    loadAnalytics();
  }, [courseId, period]);

  const loadAnalytics = async () => {
    setLoading(true);
    setError(null);
    try {
      // UPDATED: API endpoints now support both C-0000001 and numeric IDs
      const [courseRes, analyticsRes] = await Promise.all([
        axiosInstance.get(`/api/lms/courses/${courseId}`),
        axiosInstance.get(`/api/lms/courses/${courseId}/analytics`, {
          params: { period }
        })
      ]);

      setCourse(courseRes.data.data);
      setAnalytics(analyticsRes.data.data);
    } catch (error) {
      console.error('Error loading analytics:', error);
      setError(error.response?.data?.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      // UPDATED: New export endpoint
      const response = await axiosInstance.get(`/api/lms/courses/${courseId}/export`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      // Use course_identity if available, otherwise use courseId
      const filename = `course_${course?.course_identity || courseId}_report.csv`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error exporting report:', error);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 5, mt: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3, mt: 10 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate(`/chatbot/${chatbotId}/lms`)}
        >
          Back to LMS
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, mt: 10 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Button
            startIcon={<BackIcon />}
            onClick={() => navigate(`/chatbot/${chatbotId}/lms`)}
            sx={{ mr: 2 }}
          >
            Back
          </Button>
          <Box sx={{ ml: 2 }}>
            <Typography variant="h4" fontWeight={700}>
              {course?.title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Course Analytics
              {/* NEW: Display course identity */}
              {course?.course_identity && (
                <Chip 
                  label={course.course_identity} 
                  size="small" 
                  sx={{ ml: 1 }}
                  variant="outlined"
                />
              )}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Period</InputLabel>
            <Select value={period} onChange={(e) => setPeriod(e.target.value)} label="Period">
              <MenuItem value="7_days">Last 7 Days</MenuItem>
              <MenuItem value="30_days">Last 30 Days</MenuItem>
              <MenuItem value="this_month">This Month</MenuItem>
              <MenuItem value="this_year">This Year</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
          >
            Export CSV
          </Button>
        </Box>
      </Box>

      {/* Overview Cards - UPDATED: Using new analytics structure */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h4" color="primary" fontWeight={700}>
                {safeNumber(analytics?.enrollment_stats?.total_enrollments)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Enrollments
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {safeNumber(analytics?.enrollment_stats?.recent_enrollments)} recent
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h4" color="success.main" fontWeight={700}>
                {safeNumber(analytics?.enrollment_stats?.completed_enrollments)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Completions
              </Typography>
              <Typography variant="caption" color="success.main">
                {safeToFixed(analytics?.enrollment_stats?.completion_rate)}% rate
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h4" color="warning.main" fontWeight={700}>
                {safeNumber(analytics?.enrollment_stats?.active_enrollments)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Active Enrollments
              </Typography>
              <Typography variant="caption" color="text.secondary">
                In progress
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h4" color="info.main" fontWeight={700}>
                {safeToFixed(analytics?.enrollment_stats?.average_progress)}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Average Progress
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Overall completion
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts */}
      <Grid container spacing={3}>
        {/* Module Performance - UPDATED: Using modules_stats */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Module Performance
            </Typography>
            {safeArray(analytics?.modules_stats).length > 0 ? (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Module</TableCell>
                      <TableCell align="right">Students</TableCell>
                      <TableCell align="right">Completed</TableCell>
                      <TableCell align="right">Completion Rate</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {safeArray(analytics?.modules_stats).map((module, index) => (
                      <TableRow key={module.module_id || index}>
                        <TableCell>{module.module_title || 'Unknown Module'}</TableCell>
                        <TableCell align="right">
                          {safeNumber(module.total_students)}
                        </TableCell>
                        <TableCell align="right">
                          {safeNumber(module.completed_students)}
                        </TableCell>
                        <TableCell align="right">
                          <Chip
                            label={`${safeToFixed(module.completion_rate)}%`}
                            color={
                              safeNumber(module.completion_rate) >= 80 ? 'success' : 
                              safeNumber(module.completion_rate) >= 50 ? 'warning' : 'error'
                            }
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  No module data available
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Test Performance - UPDATED: Using test_stats */}
        {safeArray(analytics?.test_stats).length > 0 && (
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Test Performance
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Module</TableCell>
                      <TableCell>Test</TableCell>
                      <TableCell align="right">Attempts</TableCell>
                      <TableCell align="right">Passed</TableCell>
                      <TableCell align="right">Pass Rate</TableCell>
                      <TableCell align="right">Avg Score</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {safeArray(analytics?.test_stats).map((test, index) => (
                      <TableRow key={index}>
                        <TableCell>{test.module_title || 'Unknown'}</TableCell>
                        <TableCell>{test.test_title || 'Assessment'}</TableCell>
                        <TableCell align="right">
                          {safeNumber(test.total_attempts)}
                        </TableCell>
                        <TableCell align="right">
                          {safeNumber(test.passed_attempts)}
                        </TableCell>
                        <TableCell align="right">
                          <Chip
                            label={`${safeToFixed(test.pass_rate)}%`}
                            color={
                              safeNumber(test.pass_rate) >= 80 ? 'success' : 
                              safeNumber(test.pass_rate) >= 60 ? 'warning' : 'error'
                            }
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="right">
                          {safeToFixed(test.average_score)}%
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default CourseAnalytics;